import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { FaCreditCard, FaTrash, FaPlus, FaLock, FaCheck, FaTimes, FaGift, FaEuroSign, FaEnvelope, FaUser } from 'react-icons/fa';
import api from '../../../services/api';

// Charger Stripe avec la clé publique
const stripePromise = loadStripe(
  process.env.REACT_APP_ENV === "production"
    ? "pk_live_51PLuQiHzL8gubNCfOBaaohZ0Vwz2Rti3HVD5YSdxui9saiRuPA1T0XRfNKEfvmlxSeKBHJX3uGBcOGdd769RoChF00t2XrnVLW"
    : "pk_test_51PLuQiHzL8gubNCfacSYt0jDJM3KgLsMpR3lYmVdKJTu57m1WCbR9NUnIIbKZeLt7Jlf8rZ1CQyH57REdDy6Z5wU00h2owk64H"
);

// Style pour le CardElement
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
  hidePostalCode: true,
};

// Composant interne pour le formulaire de carte
const CardForm = ({ onCardSaved, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data } = await api.post('/free-amount/setup-intent');
      const { clientSecret } = data;

      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
      } else if (setupIntent.status === 'succeeded') {
        onCardSaved();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement de la carte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-gray-300 rounded-lg bg-white">
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>

      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!stripe || loading}
          className={`flex-1 py-3 rounded-lg font-medium flex items-center justify-center gap-2 ${
            loading
              ? 'bg-gray-300 text-gray-500'
              : 'bg-purple-600 text-white hover:bg-purple-700'
          }`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              Enregistrement...
            </>
          ) : (
            <>
              <FaLock className="w-4 h-4" />
              Enregistrer la carte
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Annuler
        </button>
      </div>

      <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
        <FaLock className="w-3 h-3" />
        Paiement sécurisé par Stripe
      </p>
    </form>
  );
};

// Composant pour gérer le 3DS
const Handle3DS = ({ clientSecret, invoiceId, recipientInfo, onSuccess, onError }) => {
  const stripe = useStripe();
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const confirm3DS = async () => {
      if (!stripe) return;

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);

      if (error) {
        onError(error.message);
      } else if (paymentIntent.status === 'succeeded') {
        try {
          const { data } = await api.post('/pro-gift-card/confirm-payment', {
            invoiceId,
            recipientEmail: recipientInfo.email,
            recipientFirstName: recipientInfo.firstName,
            recipientLastName: recipientInfo.lastName,
            giftCardAmount: recipientInfo.amount,
          });
          if (data.success) {
            onSuccess(data);
          } else {
            onError(data.message || 'Erreur lors de la confirmation');
          }
        } catch (err) {
          onError(err.response?.data?.message || 'Erreur lors de la confirmation');
        }
      }
      setProcessing(false);
    };

    confirm3DS();
  }, [stripe, clientSecret, invoiceId, recipientInfo, onSuccess, onError]);

  if (processing) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-500 border-t-transparent mb-4"></div>
        <p className="text-gray-600">Vérification 3D Secure en cours...</p>
      </div>
    );
  }

  return null;
};

// Composant principal
const ProGiftCardContent = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const [step, setStep] = useState('info'); // 'info' | 'payment' | 'success'
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientFirstName, setRecipientFirstName] = useState('');
  const [recipientLastName, setRecipientLastName] = useState('');
  const [amount, setAmount] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const [requires3DS, setRequires3DS] = useState(null);
  const [successData, setSuccessData] = useState(null);

  // Calcul des montants avec commission Fideliz (10% + TVA 20%)
  const calculateAmounts = (giftCardAmount) => {
    const giftCard = parseFloat(giftCardAmount) || 0;
    const commissionHT = giftCard * 0.1;
    const commissionTVA = commissionHT * 0.2;
    const commissionTTC = commissionHT + commissionTVA;
    const totalTTC = giftCard + commissionTTC;
    return {
      giftCard,
      commissionHT,
      commissionTVA,
      commissionTTC,
      totalTTC,
    };
  };

  const amounts = calculateAmounts(amount);

  useEffect(() => {
    if (step === 'payment') {
      loadPaymentMethods();
    }
  }, [step]);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/free-amount/payment-methods');
      setPaymentMethods(data.paymentMethods || []);
      if (data.paymentMethods?.length > 0) {
        setSelectedCard(data.paymentMethods[0].id);
      }
    } catch (err) {
      console.error('Erreur chargement cartes:', err);
      setError('Erreur lors du chargement des cartes');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('Supprimer cette carte ?')) return;

    try {
      await api.delete(`/free-amount/payment-methods/${cardId}`);
      setPaymentMethods(paymentMethods.filter((c) => c.id !== cardId));
      if (selectedCard === cardId) {
        setSelectedCard(paymentMethods[0]?.id || null);
      }
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const handleCardSaved = () => {
    setShowAddCard(false);
    loadPaymentMethods();
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+\.?\d{0,2}$/.test(value)) {
      setAmount(value);
    }
  };

  const handleContinueToPayment = () => {
    if (!recipientEmail || !recipientFirstName || !recipientLastName) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    if (!amount || parseFloat(amount) < 10) {
      setError('Le montant minimum est de 10 EUR');
      return;
    }
    if (!confirmed) {
      setError('Veuillez confirmer que l\'adresse email est correcte');
      return;
    }
    setError('');
    setStep('payment');
  };

  const handlePay = async () => {
    if (!selectedCard || !amount) return;

    setPaying(true);
    setError('');

    try {
      const { data } = await api.post('/pro-gift-card/pay', {
        recipientEmail,
        recipientFirstName,
        recipientLastName,
        amount: parseFloat(amount),
        paymentMethodId: selectedCard,
      });

      if (data.success && data.status === 'succeeded') {
        setSuccessData(data);
        setStep('success');
      } else if (data.status === 'requires_action') {
        setRequires3DS({
          clientSecret: data.clientSecret,
          invoiceId: data.invoiceId,
          recipientInfo: {
            email: recipientEmail,
            firstName: recipientFirstName,
            lastName: recipientLastName,
            amount: parseFloat(amount),
          },
        });
      } else {
        setError(data.message || 'Paiement non complété');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du paiement');
    } finally {
      setPaying(false);
    }
  };

  // Étape 1: Saisie infos destinataire
  if (step === 'info') {
    return (
      <div className="space-y-5">
        {/* Email */}
        <div>
          <label className="block mb-1.5 text-sm font-semibold text-gray-700">
            Email du destinataire
          </label>
          <div className="relative">
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pl-11"
              placeholder="email@exemple.com"
              autoFocus
            />
            <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          </div>
        </div>

        {/* Prénom */}
        <div>
          <label className="block mb-1.5 text-sm font-semibold text-gray-700">
            Prénom du destinataire
          </label>
          <div className="relative">
            <input
              type="text"
              value={recipientFirstName}
              onChange={(e) => setRecipientFirstName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pl-11"
              placeholder="Prénom"
            />
            <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          </div>
        </div>

        {/* Nom */}
        <div>
          <label className="block mb-1.5 text-sm font-semibold text-gray-700">
            Nom du destinataire
          </label>
          <div className="relative">
            <input
              type="text"
              value={recipientLastName}
              onChange={(e) => setRecipientLastName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pl-11"
              placeholder="Nom"
            />
            <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          </div>
        </div>

        {/* Montant */}
        <div>
          <label className="block mb-1.5 text-sm font-semibold text-gray-700">
            Montant de la carte cadeau (min. 10 EUR)
          </label>
          <div className="relative">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={handleAmountChange}
              className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent font-medium pr-12"
              placeholder="0.00"
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <FaEuroSign className="text-gray-400 w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Aperçu des montants */}
        {amount && parseFloat(amount) >= 10 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium text-gray-700 mb-3">Récapitulatif du paiement :</p>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Carte cadeau Glady</span>
              <span className="font-medium">{amounts.giftCard.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Commission Fideliz (10%)</span>
              <span className="font-medium">{amounts.commissionHT.toFixed(2)} € HT</span>
            </div>
            <div className="flex justify-between text-sm text-gray-500">
              <span className="pl-4">TVA 20%</span>
              <span>{amounts.commissionTVA.toFixed(2)} €</span>
            </div>
            <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between">
              <span className="font-semibold text-gray-900">Total à payer</span>
              <span className="font-bold text-lg text-purple-600">{amounts.totalTTC.toFixed(2)} € TTC</span>
            </div>
          </div>
        )}

        {/* Checkbox de confirmation */}
        <label className="flex items-start gap-3 cursor-pointer bg-orange-50 border border-orange-200 rounded-lg p-4">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <span className="text-sm text-gray-700">
            Je confirme que l'adresse email est correcte. Cette opération est définitive, sans retour ni remboursement possible.
          </span>
        </label>

        {error && (
          <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleContinueToPayment}
            disabled={!recipientEmail || !recipientFirstName || !recipientLastName || !amount || parseFloat(amount) < 10 || !confirmed}
            className={`flex-1 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${
              !recipientEmail || !recipientFirstName || !recipientLastName || !amount || parseFloat(amount) < 10 || !confirmed
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            Continuer vers le paiement
          </button>
        </div>
      </div>
    );
  }

  // Étape succès
  if (step === 'success') {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaCheck className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Carte cadeau envoyée !</h3>
        <p className="text-gray-600 mb-2">
          Carte cadeau de {amounts.giftCard.toFixed(2)} € envoyée à {recipientEmail}
        </p>
        <p className="text-sm text-gray-500 mb-4">
          Total payé : {amounts.totalTTC.toFixed(2)} € TTC (dont {amounts.commissionTTC.toFixed(2)} € de commission)
        </p>
        {successData?.invoiceUrl && (
          <a
            href={successData.invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-600 hover:underline text-sm mb-4 block"
          >
            Voir la facture
          </a>
        )}
        <button
          onClick={() => onSuccess(successData)}
          className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700"
        >
          Terminer
        </button>
      </div>
    );
  }

  // Étape 2: Paiement
  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Récapitulatif */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-1 mb-2">
        <p className="text-sm text-gray-600">
          Destinataire : <span className="font-semibold text-gray-900">{recipientFirstName} {recipientLastName}</span>
        </p>
        <p className="text-sm text-gray-600">
          Email : <span className="font-semibold text-gray-900">{recipientEmail}</span>
        </p>
      </div>

      {/* Détail facture */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
        <p className="text-sm font-semibold text-gray-700 mb-3">Détail de la facture</p>

        <div className="flex justify-between text-sm">
          <div>
            <span className="text-gray-700">Carte cadeau Glady</span>
            <span className="ml-2 text-xs text-gray-400">(sans TVA)</span>
          </div>
          <span className="font-medium">{amounts.giftCard.toFixed(2)} €</span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-gray-700">Commission Fideliz (10%)</span>
          <span className="font-medium">{amounts.commissionHT.toFixed(2)} € HT</span>
        </div>
        <div className="flex justify-between text-sm text-gray-500 pl-4">
          <span>TVA 20%</span>
          <span>{amounts.commissionTVA.toFixed(2)} €</span>
        </div>

        <div className="border-t border-gray-300 pt-3 mt-3 flex justify-between items-center">
          <span className="font-bold text-gray-900">Total TTC</span>
          <span className="text-2xl font-bold text-purple-600">{amounts.totalTTC.toFixed(2)} €</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {requires3DS ? (
        <Handle3DS
          clientSecret={requires3DS.clientSecret}
          invoiceId={requires3DS.invoiceId}
          recipientInfo={requires3DS.recipientInfo}
          onSuccess={(data) => {
            setSuccessData(data);
            setStep('success');
            setRequires3DS(null);
          }}
          onError={(msg) => {
            setError(msg);
            setRequires3DS(null);
          }}
        />
      ) : showAddCard ? (
        <CardForm onCardSaved={handleCardSaved} onCancel={() => setShowAddCard(false)} />
      ) : (
        <>
          {/* Liste des cartes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Choisissez une carte
            </label>

            {paymentMethods.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                Aucune carte enregistrée
              </p>
            ) : (
              <div className="space-y-2">
                {paymentMethods.map((card) => (
                  <div
                    key={card.id}
                    onClick={() => setSelectedCard(card.id)}
                    className={`flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition ${
                      selectedCard === card.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <FaCreditCard className="text-gray-500 w-6 h-6" />
                      <div>
                        <p className="font-medium capitalize">
                          {card.brand} **** {card.last4}
                        </p>
                        <p className="text-sm text-gray-500">
                          Expire {card.expMonth}/{card.expYear}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedCard === card.id && (
                        <FaCheck className="text-purple-500" />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCard(card.id);
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bouton ajouter une carte */}
            <button
              onClick={() => setShowAddCard(true)}
              className="mt-3 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-500 hover:text-purple-500 flex items-center justify-center gap-2 transition"
            >
              <FaPlus className="w-4 h-4" />
              Ajouter une nouvelle carte
            </button>
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={() => setStep('info')}
              className="px-6 py-4 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300"
            >
              Retour
            </button>
            <button
              onClick={handlePay}
              disabled={!selectedCard || paying}
              className={`flex-1 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 ${
                !selectedCard || paying
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-500 text-white hover:bg-green-600'
              }`}
            >
              {paying ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Paiement en cours...
                </>
              ) : (
                <>
                  <FaLock className="w-5 h-5" />
                  Payer {amounts.totalTTC.toFixed(2)} € TTC
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Modal wrapper
const ProGiftCardModal = ({ isOpen, onClose, onSuccess }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FaGift className="w-5 h-5 text-purple-500" />
            Envoyer une carte cadeau
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FaTimes className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          <Elements stripe={stripePromise}>
            <ProGiftCardContent
              onSuccess={onSuccess}
              onCancel={onClose}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default ProGiftCardModal;
